import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useLocation } from "react-router-dom";

// Prefer CRA-style env if it exists at build time; otherwise choose by hostname (sandbox vs main).
const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL && String(process.env.REACT_APP_BACKEND_URL)) ||
  ((typeof window !== 'undefined' && String(window.location.hostname).indexOf('ai-translation-frontend-sandbox') !== -1)
    ? 'https://ai-translation-backend-sandbox.onrender.com'
    : 'https://ai-translation-backend.onrender.com');

// Fallback tiers (used only if /billing/pricing is unavailable)
const FALLBACK_TIERS = [
  { credits: 10, subtotal: 10.00, fees: +(10.00 * 0.049).toFixed(2), grand_total: +(10.00 * 1.049).toFixed(2) },
  { credits: 50, subtotal: 47.50, fees: +(47.50 * 0.049).toFixed(2), grand_total: +(47.50 * 1.049).toFixed(2) },
  { credits: 100, subtotal: 90.00, fees: +(90.00 * 0.049).toFixed(2), grand_total: +(90.00 * 1.049).toFixed(2) },
  { credits: 200, subtotal: 170.00, fees: +(170.00 * 0.049).toFixed(2), grand_total: +(170.00 * 1.049).toFixed(2) },
  { credits: 300, subtotal: 240.00, fees: +(240.00 * 0.049).toFixed(2), grand_total: +(240.00 * 1.049).toFixed(2) },
  { credits: 400, subtotal: 300.00, fees: +(300.00 * 0.049).toFixed(2), grand_total: +(300.00 * 1.049).toFixed(2) },
  { credits: 500, subtotal: 350.00, fees: +(350.00 * 0.049).toFixed(2), grand_total: +(350.00 * 1.049).toFixed(2) },
];

const fmtUSD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export default function PurchaseCredits({ billingStatus } = {}) {
  const { jwt, user, logout, creditsTick } = useAuth();

  const location = useLocation();

  // Normalize billing status from either:
  //  - explicit prop (preferred), or
  //  - the current route (/billing/cancel, /billing/success)
  const routeStatus =
    location && location.pathname === "/billing/cancel"
      ? "cancel"
      : location && location.pathname === "/billing/success"
      ? "success"
      : null;

  const normalizedStatus = (billingStatus || routeStatus || "").toString().trim().toLowerCase();

  const isCancel = normalizedStatus === "cancel";
  const isSuccess = normalizedStatus === "success";
  const [credits, setCredits] = useState(10); // default bundle size
  const [provider, setProvider] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Pricing from server; fallback if unavailable
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [feesRate, setFeesRate] = useState(0.049); // informational only
  const [loadingPricing, setLoadingPricing] = useState(true); // NEW: skeleton flag

  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  // Load authoritative pricing from backend
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingPricing(true); // show skeleton while fetching
        const res = await axios.get(`${API_BASE}/billing/pricing`, { headers });
        const data = res.data || {};
        // Expect: { currency, fees_rate, tiers: [{credits, subtotal, fees, grand_total}], allowed_credits: [...] }
        if (data && Array.isArray(data.tiers) && data.tiers.length > 0) {
          if (mounted) {
            setTiers(
              [...data.tiers].sort((a, b) => (a.credits || 0) - (b.credits || 0))
            );
            if (typeof data.fees_rate === "number") setFeesRate(data.fees_rate);
            // If current selection isn’t in allowed list, snap to first tier
            const allowed = (data.allowed_credits || data.tiers.map(t => t.credits)).map(Number);
            if (!allowed.includes(Number(credits))) {
              setCredits(Number(allowed[0]));
            }
          }
        }
      } catch {
        // Silent fallback to local tiers
      } finally {
        if (mounted) setLoadingPricing(false);
      }
    })();
    return () => { mounted = false; };
  }, []); // load once

  // Selected tier from the current tiers array
  const selectedTier = useMemo(
    () => tiers.find(t => Number(t.credits) === Number(credits)) || tiers[0],
    [tiers, credits]
  );

  // Discount % from subtotal vs base ($1 per credit base)
  const discountPct = useMemo(() => {
    const base = Number(selectedTier?.credits || 0);
    const sub = Number(selectedTier?.subtotal || 0);
    if (!base || base <= 0) return 0;
    const pct = (1 - (sub / base)) * 100;
    return Math.max(0, Math.round(pct));
  }, [selectedTier]);

  const savings = useMemo(() => {
    const base = Number(selectedTier?.credits || 0);
    const sub = Number(selectedTier?.subtotal || 0);
    return +(Math.max(0, base - sub)).toFixed(2);
  }, [selectedTier]);

  const handleBuy = async () => {
    setMsg("");
    if (!credits || credits <= 0) {
      setMsg("Please select a credit bundle.");
      return;
    }
    setBusy(true);
    try {
      // Only send { credits }; server enforces price/fees.
      if (provider === "stripe") {
        const res = await axios.post(`${API_BASE}/billing/checkout/stripe`, { credits }, { headers });
        window.location.href = res.data.checkout_url;
        return;
      } else {
        const res = await axios.post(`${API_BASE}/billing/checkout/paypal`, { credits }, { headers });
        window.location.href = res.data.approve_url;
        return;
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Unable to start checkout.");
    } finally {
      setBusy(false);
    }
  };

  // Dropdown labels show subtotal + discount; summary shows fees & grand total
  const formatOptionLabel = (tier) => {
    if (!tier) return "";
    const pct = Math.max(0, Math.round((1 - (tier.subtotal / tier.credits)) * 100));
    return pct === 0
      ? `${tier.credits} Credits (${fmtUSD.format(tier.subtotal)})`
      : `${tier.credits} Credits (${fmtUSD.format(tier.subtotal)}, ${pct}% Discount)`;
  };

  // Skeleton block used during pricing load
  const SummarySkeleton = () => (
    <div className="rounded-xl border p-4 bg-white shadow-sm animate-pulse">
      <div className="flex items-baseline justify-between">
        <div className="font-semibold">Selected</div>
        <div className="h-5 w-24 bg-gray-200 rounded" />
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <div className="font-semibold">Discount</div>
        <div className="h-5 w-28 bg-gray-200 rounded" />
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <div className="font-semibold">Subtotal</div>
        <div className="h-6 w-28 bg-gray-200 rounded" />
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <div className="font-semibold">Taxes and Fees</div>
        <div className="h-5 w-24 bg-gray-200 rounded" />
      </div>
      <div className="my-3 border-t" />
      <div className="flex items-baseline justify-between">
        <div className="font-semibold">Grand Total</div>
        <div className="h-7 w-32 bg-gray-200 rounded" />
      </div>
      <div className="mt-2 h-3 w-40 bg-gray-200 rounded" />
    </div>
  );

  return (
    <div className="mx-auto mt-6 px-4">
      {/* App-style shell header (match App.jsx main UI) */}
      <div className="bg-white border-b">
        <div className="pt-3 mb-4 px-4 max-w-screen-lg mx-auto">
          <div className="flex items-center justify-between">

            {/* Left: compact logo (click = go to Home) */}
            <a
              href="/"
              className="flex items-center focus:outline-none"
              aria-label="Go to Home"
            >
              <img
                src="/zabflow2.png"
                alt="ZabFlow AI™ Logo"
                className="h-12 md:h-14 w-auto select-none cursor-pointer"
              />
            </a>

            {/* Right: user info + credits + actions */}
            {user ? (
              <div className="text-right pr-2">
                <div>
                  Logged in as: <b>{user.email}</b>

                  <div className="text-gray-700">
                    Current Mode:{" "}
                    <b>{user?.uses_custom_workflow ? "Custom Workflow" : "Generic Workflow"}</b>
                  </div>

                  <span>
                    Credits Remaining:{" "}
                    <b>
                      🪙{(user.credits ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </b>
                  </span>
                </div>

                {logout ? (
                  <button
                    onClick={logout}
                    className="underline mt-1"
                    style={{ color: "#00a7e1" }}
                    type="button"
                  >
                    Logout
                  </button>
                ) : null}

                {/* Ensure credits always re-render by referencing creditsTick */}
                <span style={{ display: "none" }}>{creditsTick}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Navigation links (so users don’t get stuck) */}
        <div className="max-w-screen-lg mx-auto flex justify-start mb-4 gap-2 px-4 flex-wrap">
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/?tab=main">
            Translate
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/?tab=projectRequests">
            Custom Requests
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/?tab=aiRequests">
            A.I. Requests
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/help">
            Help
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/about">
            About Zab
          </a>
          <a className="px-4 py-2 rounded-t hover:opacity-90" style={{ backgroundColor: "#f3f4f6" }} href="/settings">
            Settings
          </a>
        </div>
      </div>

      <div className="p-6 max-w-xl mx-auto space-y-6">
        {isCancel ? (
          <div className="p-4 rounded bg-yellow-50 border border-yellow-200 text-yellow-900">
            <div className="font-semibold text-lg">Payment canceled</div>
            <div className="text-sm mt-1">No charges were made. You can try again anytime.</div>
          </div>
        ) : null}

        {isSuccess ? (
          <div
            className="p-4 rounded border text-green-900"
            style={{ backgroundColor: "#dcfce7", borderColor: "#86efac" }}
          >
            <div className="font-semibold text-lg">Payment successful</div>
            <div className="text-sm mt-1">
              Your credits will appear shortly. If your balance doesn’t update, refresh the page.
            </div>
          </div>
        ) : null}

        {/* Back to main app */}
        <div>
          <a
            href="/"
            className="bg-gray-300 px-3 py-2 rounded hover:bg-gray-400 inline-block"
          >
            {"< Back"}
          </a>
        </div>

        <h2 className="text-2xl font-bold">Buy Credits</h2>
      <div className="text-sm text-gray-600 font-bold">
        Credits are used to pay for automated AI translation services only. Do not purchase credits if you plan to use human review or document formatting services as they will not work. No payment information is stored on our systems. Purchases are non-refundable.
      </div>

      <div>
        <label className="block font-medium">Amount of Credits to Purchase:</label>
        <select
          value={credits}
          onChange={(e) => setCredits(parseInt(e.target.value || "10", 10))}
          className="w-full border rounded p-2 bg-white"
          disabled={loadingPricing}
        >
          {loadingPricing ? (
            <option>Loading pricing…</option>
          ) : (
            tiers.map((tier) => (
              <option key={tier.credits} value={tier.credits}>
                {formatOptionLabel(tier)}
              </option>
            ))
          )}
        </select>
        <div className="text-xs text-gray-500 mt-1">
          {loadingPricing ? "Loading pricing…" : "Choose one of the preset credit bundles."}
        </div>
      </div>

      {/* Summary panel */}
      {loadingPricing ? (
        <SummarySkeleton />
      ) : (
        <div className="rounded-xl border p-4 bg-white shadow-sm">
          <div className="flex items-baseline justify-between">
            <div className="font-semibold">Selected</div>
            <div className="text-lg font-bold">{selectedTier?.credits} credits</div>
          </div>

          <div className="mt-2 flex items-baseline justify-between">
            <div className="font-semibold">Discount</div>
            <div className="text-lg font-bold" style={{ color: "#94c83d" }}>
              {discountPct}%{savings > 0 ? ` (you save ${fmtUSD.format(savings)})` : ""}
            </div>
          </div>

          {/* Subtotal: before fees */}
          <div className="mt-2 flex items-baseline justify-between">
            <div className="font-semibold">Subtotal</div>
            <div className="text-xl font-bold">{fmtUSD.format(Number(selectedTier?.subtotal || 0))}</div>
          </div>

          {/* Taxes and Fees */}
          <div className="mt-2 flex items-baseline justify-between">
            <div className="font-semibold">Taxes and Fees ({(feesRate * 100).toFixed(1)}%)</div>
            <div className="text-lg font-bold">{fmtUSD.format(Number(selectedTier?.fees || 0))}</div>
          </div>

          {/* Divider */}
          <div className="my-3 border-t" />

          {/* Grand Total: final amount paid */}
          <div className="flex items-baseline justify-between">
            <div className="font-semibold">Grand Total</div>
            <div className="text-2xl font-extrabold" style={{ color: "#00a7e1" }}>
              {fmtUSD.format(Number(selectedTier?.grand_total || 0))}
            </div>
          </div>

          {/* Note */}
          <div className="mt-2 text-xs text-gray-500">
            Shown total includes taxes and fees.
          </div>
        </div>
      )}

      <div>
        <label className="block font-medium mt-2">Purchase:</label>
        <div className="flex gap-4 mt-1">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="provider"
              value="stripe"
              checked={provider === "stripe"}
              onChange={() => setProvider("stripe")}
              disabled={loadingPricing}
            />
            💳 Credit Card
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="provider"
              value="paypal"
              checked={provider === "paypal"}
              onChange={() => setProvider("paypal")}
              disabled={loadingPricing}
            />
            🅿️ PayPal
          </label>
        </div>
      </div>

      <button
        onClick={handleBuy}
        disabled={busy || loadingPricing}
        className={`text-white px-4 py-2 rounded w-full ${(busy || loadingPricing) ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
        style={{ backgroundColor: "#00a7e1" }}
      >
        {busy
          ? "Starting checkout..."
          : loadingPricing
          ? "Loading pricing…"
          : `Buy Credits — ${fmtUSD.format(Number(selectedTier?.grand_total || 0))}`}
      </button>

      {msg && <div className="text-red-600 text-sm">{msg}</div>}

      <div className="text-xs text-gray-500">
        After completing payment, you’ll be redirected back to the ZabFlow app. Credits appear
        automatically. If they don’t show immediately, please wait a moment and refresh your browser.
      </div>
    </div>
  </div>
  );
}
